import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import { useToast } from '../components/Toast';

interface Props {
  projectId: string;
  onClose: () => void;
  onPromoted: () => void;
}

export function PromoteDialog({ projectId, onClose, onPromoted }: Props) {
  const toast = useToast();
  const [hostname, setHostname] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'input' | 'probing' | 'rendering' | 'done'>('input');
  const [buildRunId, setBuildRunId] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const clean = hostname.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(clean)) {
      toast.error('Enter a valid domain (e.g. demolitionmastersny.com)');
      return;
    }
    setSubmitting(true);
    setStep('probing');
    try {
      const r = await api.promoteToProduction(projectId, clean);
      setBuildRunId(r.buildRunId);
      setStep('rendering');
      toast.success(`Domain flipped → metadata-only re-render started (~5 min)`);
      onPromoted();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      toast.error(msg);
      setStep('input');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold mb-1">Connect a custom domain</h2>
        <p className="text-sm text-gray-500 mb-5">
          Before clicking <b>Promote</b>: buy the domain (Cloudflare Registrar is
          easiest), add it to the CF Pages project, and confirm HTTPS works.
        </p>

        {step === 'input' && (
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Custom domain</span>
              <input
                type="text"
                placeholder="demolitionmastersny.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                autoComplete="off"
                required
                disabled={submitting}
                autoFocus
              />
              <span className="text-xs text-gray-500 mt-1 block">
                Engine probes HTTPS at this hostname. If the cert isn't ready or
                the CF Pages mapping is missing, you'll see an error and nothing
                will change.
              </span>
            </label>

            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={submitting || !hostname.trim()}
                className="bg-brand text-white font-medium px-5 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Promoting…' : 'Promote to production'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-700 font-medium px-5 py-2 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {step === 'probing' && (
          <div className="py-6 text-sm text-gray-700">
            Probing <code className="bg-gray-100 px-1 rounded text-xs">https://{hostname}/</code> …
          </div>
        )}

        {step === 'rendering' && (
          <div className="py-2 text-sm text-gray-700 space-y-3">
            <p>
              <b>Domain flipped.</b> Engine is re-rendering 298 pages with the new
              canonical URL + permissive robots.txt. This usually takes 5 minutes.
            </p>
            <p>BuildRun: <code className="bg-gray-100 px-1 rounded text-xs font-mono">{buildRunId}</code></p>
            <p className="text-xs text-gray-500">
              When the BuildRun completes, deploy the new snapshot via the project
              detail page (Connect Custom Domain → Deploy after Build).
            </p>
            <div className="pt-2">
              <button
                type="button"
                onClick={onClose}
                className="bg-brand text-white font-medium px-5 py-2 rounded-md hover:bg-brand-dark"
              >
                Got it — back to project
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
